import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { BellIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import NotificationItem from './NotificationItem';
import Button from '../../../../../../ui/canon/Button';

const NotificationList = ({
  notifications,
  openCount,
  updateCount,
  after,
  hasNextPage,
  fetchMore,
  resetLoaded,
  loading
}) => {
  const intl = useIntl();
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Memoize the notification list to prevent unnecessary re-renders
  const notificationItems = useMemo(() => {
    return notifications.map((notification, index) => (
      <div 
        key={notification.id} 
        className={`transition-opacity duration-300 ${notification.readAt ? 'opacity-70' : 'opacity-100'}`}
      >
        <NotificationItem
          notification={notification}
          updateCount={updateCount}
          onNavigate={(notif) => {
            console.log('Navigating to notification:', notif);
            // Handle navigation based on notification type
            // This could be enhanced to route to specific pages
          }}
        />
      </div>
    ));
  }, [notifications, updateCount]);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasNextPage) return;
    
    setIsLoadingMore(true);
    
    try {
      await fetchMore({
        variables: { after },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          
          const result = { ...fetchMoreResult, loadMore: true };
          resetLoaded({
            notifications: [
              ...notifications,
              ...fetchMoreResult.getCurrentUser.notifications.edges.map(n => n.node),
            ],
            after: fetchMoreResult.getCurrentUser.notifications.pageInfo.endCursor,
            hasNextPage: fetchMoreResult.getCurrentUser.notifications.pageInfo.hasNextPage,
          });
          return result;
        },
      });
    } catch (error) {
      console.error('Failed to load more notifications:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasNextPage, fetchMore, after, resetLoaded, notifications]);

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="p-5">
      {[...Array(3)].map((_, index) => (
        <div
          key={index}
          style={{
            height: '80px',
            backgroundColor: 'var(--succession-bg-tertiary)',
            borderRadius: '12px',
            marginBottom: '12px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Shimmer effect */}
          <div
            className="absolute top-0 -left-full w-full h-full animate-[shimmer_1.5s_infinite]"
            style={{
              background: 'linear-gradient(90deg, transparent, var(--succession-hover-bg), transparent)',
            }}
          />
          
          {/* Fake content */}
          <div style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              backgroundColor: 'var(--succession-border)',
              borderRadius: '50%',
            }} />
            <div style={{ flex: 1 }}>
              <div style={{
                width: '70%',
                height: '16px',
                backgroundColor: 'var(--succession-border)',
                borderRadius: '4px',
                marginBottom: '8px',
              }} />
              <div style={{
                width: '50%',
                height: '12px',
                backgroundColor: 'var(--succession-bg-tertiary)',
                borderRadius: '4px',
              }} />
            </div>
          </div>
        </div>
      ))}
      
      <style jsx>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );

  // Empty state component
  const EmptyState = () => (
    <div style={{
      padding: '60px 40px',
      textAlign: 'center',
    }}>
      <div style={{
        width: '80px',
        height: '80px',
        backgroundColor: 'var(--succession-primary-10)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
        border: '3px solid var(--succession-primary-20)',
      }}>
        <BellIcon style={{ width: '40px', height: '40px', color: 'var(--succession-primary)' }} />
      </div>
      
      <h3 style={{
        fontSize: '20px',
        fontWeight: '700',
        color: 'var(--succession-text-primary)',
        margin: '0 0 12px',
      }}>
        {intl.formatMessage({ id: 'succession.notifications.empty_title' })}
      </h3>

      <p style={{
        fontSize: '15px',
        color: 'var(--succession-text-secondary)',
        lineHeight: '1.6',
        maxWidth: '280px',
        margin: '0 auto 24px',
      }}>
        {intl.formatMessage({ id: 'succession.notifications.empty_body' })}
      </p>
      
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '12px 20px',
        backgroundColor: 'var(--succession-primary-10)',
        borderRadius: '12px',
        border: '1px solid var(--succession-primary-20)',
        fontSize: '13px',
        color: 'var(--succession-primary)',
        fontWeight: '500',
      }}>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: 'var(--succession-primary)',
          animation: 'pulse 2s infinite',
        }} />
        <span>{intl.formatMessage({ id: 'succession.notifications.realtime_enabled' })}</span>
      </div>
      
      <style jsx>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );

  // Error state component
  const ErrorState = ({ error, onRetry }) => (
    <div style={{
      padding: '40px',
      textAlign: 'center',
    }}>
      <div style={{
        width: '64px',
        height: '64px',
        backgroundColor: 'var(--succession-error-bg)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 20px',
      }}>
        <ExclamationTriangleIcon style={{ width: '32px', height: '32px', color: 'var(--succession-error)' }} />
      </div>
      
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: 'var(--succession-error)',
        margin: '0 0 8px',
      }}>
        {intl.formatMessage({ id: 'succession.notifications.something_went_wrong' })}
      </h3>

      <p style={{
        fontSize: '14px',
        color: 'var(--succession-text-secondary)',
        margin: '0 0 20px',
      }}>
        {error?.message || intl.formatMessage({ id: 'succession.notifications.error_failed_load' })}
      </p>

      <button
        onClick={onRetry}
        style={{
          padding: '10px 20px',
          backgroundColor: 'var(--succession-primary)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = 'var(--succession-primary-hover)';
          e.target.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'var(--succession-primary)';
          e.target.style.transform = 'translateY(0)';
        }}
      >
        {intl.formatMessage({ id: 'succession.notifications.try_again' })}
      </button>
    </div>
  );

  // Main render logic
  if (loading && notifications.length === 0) {
    return <LoadingSkeleton />;
  }

  if (notifications.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Notification count summary */}
      {notifications.length > 0 && (
        <div className="px-6 py-4 bg-succession-bg-secondary border-b border-succession-border text-sm text-succession-text-secondary font-medium">
          {intl.formatMessage({ id: 'succession.notifications.notification_count_other' }, { count: notifications.length })}
          {openCount > 0 && (
            <span className="text-succession-primary">
              {' '}• {intl.formatMessage({ id: 'succession.notifications.unread' }, { count: openCount })}
            </span>
          )}
        </div>
      )}

      {/* Notifications list */}
      <div className="px-5 py-4 space-y-2">
        {notificationItems}
      </div>

      {/* Load more button */}
      {hasNextPage && (
        <div className="p-5 flex justify-center">
          <Button
            variant="primary"
            size="lg"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className={`px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)] ${
              isLoadingMore 
                ? 'bg-succession-bg-tertiary text-succession-text-muted cursor-not-allowed' 
                : 'bg-succession-primary text-white hover:bg-succession-primary-hover hover:transform hover:-translate-y-0.5'
            }`}
          >
            {isLoadingMore ? (
              <>
                <div className="w-4 h-4 border-2 border-succession-border border-t-succession-text-muted rounded-full animate-spin" />
                {intl.formatMessage({ id: 'succession.notifications.loading' })}
              </>
            ) : (
              intl.formatMessage({ id: 'succession.notifications.load_more' })
            )}
          </Button>
        </div>
      )}

    </div>
  );
};

NotificationList.propTypes = {
  notifications: PropTypes.array.isRequired,
  openCount: PropTypes.number.isRequired,
  updateCount: PropTypes.func.isRequired,
  after: PropTypes.string,
  hasNextPage: PropTypes.bool,
  fetchMore: PropTypes.func,
  resetLoaded: PropTypes.func,
  loading: PropTypes.bool,
};

NotificationList.defaultProps = {
  after: null,
  hasNextPage: false,
  fetchMore: () => {},
  resetLoaded: () => {},
  loading: false,
};

export default NotificationList; 